import {
    View
} from "react-native";
import type IRowCardProps from "./type";
import type {
    RowCardItemMappingConstantType,
    RowCardSlotStateType,
    RowCardItemMapping
} from "./type";
import stylesheet, {
    getRowCardSkeletonWidths,
    getRowCardContainerColor,
    getRowCardTitleVariant,
    getRowCardColors,
    getRowCardRadius
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import Skeleton from "../skeleton";
import Item from "../item";
import Text from "../text";

export const ROW_CARD_DEFAULT_TITLE_VARIANT: keyof NCoreUIKit.Typography = "labelMediumSize";

export const getItemPropsFromRowCard = ({
    props,
    theme
}: RowCardItemMappingConstantType): RowCardItemMapping => {
    const {
        rightTitleVariant = ROW_CARD_DEFAULT_TITLE_VARIANT,
        titleVariant = ROW_CARD_DEFAULT_TITLE_VARIANT,
        rightSubTitleVariant = "bodySmallSize",
        renderTrailing: renderTrailingProp,
        subTitleVariant = "bodyMediumSize",
        renderLeading: renderLeadingProp,
        isTransparentBackground = false,
        rightContentContainerStyle,
        leftContentContainerStyle,
        rightIcon: RightIconProp,
        subTitleNumberOfLines,
        type = "first-based",
        rightContainerStyle,
        isDisabled = false,
        isSelected = false,
        leftContainerStyle,
        rightSubTitleColor,
        rightSubTitleStyle,
        titleNumberOfLines,
        isLoading = false,
        leftSubTitleStyle,
        isInList = false,
        backgroundColor,
        rightTitleColor,
        rightTitleStyle,
        icon: IconProp,
        leftTitleStyle,
        rightIconStyle,
        rightSubTitle,
        subTitleColor,
        borderRadius,
        rightTitle,
        titleColor,
        iconColor,
        iconStyle,
        iconSize,
        subTitle,
        style,
        title,
        ...rest
    } = props;

    const {
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = theme;

    const isSelectedVisible = isSelected && !isDisabled;

    const currentColors = getRowCardColors({
        isTransparentBackground,
        rightSubTitleColor,
        rightTitleColor,
        backgroundColor,
        subTitleColor,
        titleColor,
        isDisabled,
        isSelected,
        iconColor,
        colors
    });

    const currentTitleVariant = getRowCardTitleVariant({
        isSelected: isSelectedVisible,
        titleVariant
    });

    const currentSkeletonWidths = getRowCardSkeletonWidths({
        iconSizes
    });

    const containerColor = getRowCardContainerColor({
        isTransparentBackground,
        isSelected: false,
        backgroundColor,
        isDisabled,
        colors
    });

    const isRightTitleVisible = isLoading ? rightTitle !== undefined : !!rightTitle;

    const isRightSubTitleVisible = isLoading ? rightSubTitle !== undefined : !!rightSubTitle;

    const slotState: RowCardSlotStateType = {
        iconColor: currentColors.iconColor,
        isSelected: isSelectedVisible,
        isFocusVisible: false,
        isHovered: false,
        isPressed: false,
        isDisabled,
        isLoading
    };

    const evenlyStyle = type === "evenly" ? stylesheet.evenlyActions : undefined;

    const containerStyle = containerColor ? {
        backgroundColor: containerColor
    } : undefined;

    const renderMedia = () => {
        if(renderLeadingProp) {
            return <Item.Media>
                {renderLeadingProp(slotState)}
            </Item.Media>;
        }

        if(!IconProp) {
            return null;
        }

        if(iconSize === undefined && iconColor === undefined) {
            return <Item.Media
                style={iconStyle}
                icon={IconProp}
            />;
        }

        return <Item.Media
            style={iconStyle}
        >
            <IconProp
                customColor={currentColors.iconColor}
                size={iconSize ?? iconSizes.medium}
                color={currentColors.iconColorKey}
            />
        </Item.Media>;
    };

    const renderTitleText = () => {
        if(titleVariant === ROW_CARD_DEFAULT_TITLE_VARIANT && titleColor === undefined) {
            return title;
        }

        return <Text
            customColor={currentColors.titleColor}
            customTheme={rest.customTheme}
            variant={currentTitleVariant}
        >
            {title}
        </Text>;
    };

    const renderSubTitleText = () => {
        if(subTitleVariant === "bodyMediumSize" && subTitleColor === undefined) {
            return subTitle;
        }

        return <Text
            customColor={currentColors.subTitleColor}
            customTheme={rest.customTheme}
            variant={subTitleVariant}
        >
            {subTitle}
        </Text>;
    };

    const renderTitle = () => {
        if(!isLoading && !title) {
            return null;
        }

        return <Item.Title
            numberOfLines={titleNumberOfLines ?? 0}
            style={leftTitleStyle}
        >
            {renderTitleText()}
        </Item.Title>;
    };

    const renderDescription = () => {
        if(isLoading ? subTitle === undefined : !subTitle) {
            return null;
        }

        return <Item.Description
            numberOfLines={subTitleNumberOfLines ?? 0}
            style={leftSubTitleStyle}
        >
            {renderSubTitleText()}
        </Item.Description>;
    };

    const renderRightTitle = () => {
        if(!isRightTitleVisible) {
            return null;
        }

        if(isLoading) {
            return <Skeleton
                width={currentSkeletonWidths.rightTitle}
                customTheme={rest.customTheme}
                typography={rightTitleVariant}
                variant="text"
            />;
        }

        return <Text
            customColor={currentColors.rightTitleColor}
            customTheme={rest.customTheme}
            variant={rightTitleVariant}
            style={rightTitleStyle}
            numberOfLines={1}
        >
            {rightTitle}
        </Text>;
    };

    const renderRightSubTitle = () => {
        if(!isRightSubTitleVisible) {
            return null;
        }

        if(isLoading) {
            return <Skeleton
                width={currentSkeletonWidths.rightSubTitle}
                typography={rightSubTitleVariant}
                customTheme={rest.customTheme}
                variant="text"
            />;
        }

        return <Text
            customColor={currentColors.rightSubTitleColor}
            customTheme={rest.customTheme}
            variant={rightSubTitleVariant}
            style={[
                rightSubTitleStyle,
                stylesheet.rightSubTitle
            ]}
            numberOfLines={1}
        >
            {rightSubTitle}
        </Text>;
    };

    const renderRight = () => {
        if(!isRightTitleVisible && !isRightSubTitleVisible) {
            return null;
        }

        return <Item.Actions
            style={[
                rightContainerStyle,
                evenlyStyle
            ]}
        >
            <View
                style={[
                    rightContentContainerStyle,
                    stylesheet.rightContent
                ]}
            >
                {renderRightTitle()}
                {renderRightSubTitle()}
            </View>
        </Item.Actions>;
    };

    const renderTrailing = () => {
        if(!renderTrailingProp) {
            return null;
        }

        return <Item.Actions>
            {renderTrailingProp(slotState)}
        </Item.Actions>;
    };

    const renderRightIcon = () => {
        if(!RightIconProp) {
            return null;
        }

        return <Item.Actions>
            <View
                style={[
                    rightIconStyle,
                    stylesheet.rightIconContainer
                ]}
                aria-hidden={true}
            >
                <RightIconProp
                    customColor={currentColors.iconColor}
                    color={currentColors.iconColorKey}
                    size={iconSizes.small}
                />
            </View>
        </Item.Actions>;
    };

    return {
        children: <>
            {renderMedia()}
            <Item.Content
                style={[
                    leftContainerStyle,
                    leftContentContainerStyle
                ]}
            >
                {renderTitle()}
                {renderDescription()}
            </Item.Content>
            {renderRight()}
            {renderTrailing()}
            {renderRightIcon()}
        </>,
        itemProps: {
            ...rest,
            borderRadius: getRowCardRadius({
                borderRadius,
                isInList,
                radiuses,
                borders,
                spaces
            }),
            style: [
                containerStyle,
                style
            ],
            isDisabled,
            isSelected,
            isLoading
        }
    };
};

export const useItemPropsFromRowCard = (props: IRowCardProps): RowCardItemMapping => {
    const {
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(props.customTheme);

    return getItemPropsFromRowCard({
        theme: {
            iconSizes,
            radiuses,
            borders,
            colors,
            spaces
        },
        props
    });
};
